/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * PublicIndexConfigModal — modal for configuring the /p/ index page.
 * Auto-fills page_title from appName, subtitle from appDescription.
 * Accent colour picker shows predefined Arcee theme swatches.
 */

import React, { useState, useEffect } from 'react';
import { usePublicIndexConfig, savePublicIndexConfig } from '../../hooks/usePublicViewConfig';
import { useQueryClient } from '@tanstack/react-query';
import { useTheme } from '../../theme/ThemeContext';

export default function PublicIndexConfigModal({ appId, appName, appDescription, onClose }) {
  const { theme } = useTheme();
  const queryClient = useQueryClient();
  const { data: existing, isLoading } = usePublicIndexConfig(appId);

  const [cfg, setCfg] = useState({
    page_title: '',
    subtitle:   '',
    layout:     'grid',
    is_active:  true,
    seo:        {},
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg]       = useState(null);

  // Seed from saved config or fall back to app name/description
  useEffect(() => {
    if (existing && Object.keys(existing).length > 0) {
      setCfg(prev => ({
        ...prev,
        ...existing,
        // Only use app defaults when the saved value is still blank
        page_title: existing.page_title || appName || '',
        subtitle:   existing.subtitle   || appDescription || '',
      }));
    } else {
      // No saved config yet — pre-fill from application metadata
      setCfg(prev => ({
        ...prev,
        page_title: appName        || '',
        subtitle:   appDescription || '',
      }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existing, appName, appDescription]);

  function update(key, value) {
    setCfg(prev => ({ ...prev, [key]: value }));
  }

  async function handleSave() {
    setSaving(true);
    setMsg(null);
    try {
      await savePublicIndexConfig({ ...cfg, application_id: appId, theme: theme.name });
      queryClient.invalidateQueries({ queryKey: ['publicIndexConfig', appId] });
      setMsg({ type: 'success', text: 'Index page config saved.' });
      setTimeout(onClose, 1000);
    } catch (err) {
      setMsg({ type: 'error', text: err.message || 'Save failed.' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="modal d-block"
      tabIndex="-1"
      style={{ background: 'rgba(0,0,0,0.4)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content border-0 shadow-lg">
          <div className="modal-header border-bottom">
            <h5 className="modal-title fw-bold">
              <i className="bi bi-house me-2" style={{ color: theme.primary }} />
              Configure Index Page (/p/)
            </h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>

          <div className="modal-body">
            {isLoading ? (
              <div className="text-center py-4"><span className="spinner-border" /></div>
            ) : (
              <div className="d-flex flex-column gap-3">

                {/* Page title + subtitle */}
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label small fw-semibold">
                      Directory Page Title
                      {appName && cfg.page_title === appName && (
                        <span className="ms-2 badge bg-light text-muted border" style={{ fontSize: '0.68rem', fontWeight: 400 }}>
                          auto-filled from app name
                        </span>
                      )}
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={cfg.page_title || ''}
                      onChange={e => update('page_title', e.target.value)}
                      placeholder={appName || 'Our Public Pages'}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small fw-semibold">
                      Subtitle
                      {appDescription && cfg.subtitle === appDescription && (
                        <span className="ms-2 badge bg-light text-muted border" style={{ fontSize: '0.68rem', fontWeight: 400 }}>
                          auto-filled from app description
                        </span>
                      )}
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={cfg.subtitle || ''}
                      onChange={e => update('subtitle', e.target.value)}
                      placeholder={appDescription || 'Browse all available pages'}
                    />
                  </div>
                </div>

                {/* Layout */}
                <div>
                  <label className="form-label small fw-semibold">Layout</label>
                  <div className="d-flex gap-3">
                    {[
                      { value: 'grid', label: 'Grid (3 columns)', icon: 'bi-grid-3x3' },
                      { value: 'list', label: 'List (full-width)', icon: 'bi-list-ul' },
                    ].map(opt => (
                      <div key={opt.value} className="form-check">
                        <input
                          type="radio"
                          className="form-check-input"
                          id={`layout-${opt.value}`}
                          name="index_layout"
                          value={opt.value}
                          checked={cfg.layout === opt.value}
                          onChange={() => update('layout', opt.value)}
                        />
                        <label className="form-check-label small" htmlFor={`layout-${opt.value}`}>
                          <i className={`bi ${opt.icon} me-1`} />
                          {opt.label}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Active toggle */}
                <div className="form-check form-switch">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="index-is-active"
                    checked={cfg.is_active !== false}
                    onChange={e => update('is_active', e.target.checked)}
                  />
                  <label className="form-check-label small fw-semibold" htmlFor="index-is-active">
                    Index page active (visitors can browse /p/)
                  </label>
                </div>

                {/* Member link — a page reachable from every public page's
                    navbar. Leave the label or the page blank and nothing is
                    added to the navbar at all, which is how every application
                    that has not configured one behaves. */}
                <div className="border-top pt-3">
                  <label className="form-label small fw-semibold mb-1">
                    Member link
                  </label>
                  <p className="text-muted mb-2" style={{ fontSize: 12 }}>
                    Adds one link to the public navbar on every page — the index,
                    table listings and record pages. Point it at a private page so
                    visitors are asked to sign in. Leave blank for none.
                  </p>
                  <div className="row g-2">
                    <div className="col-md-5">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Label, e.g. My registrations"
                        value={cfg.member_link?.label || ''}
                        onChange={e => update('member_link', {
                          ...(cfg.member_link || {}), label: e.target.value,
                        })}
                      />
                    </div>
                    <div className="col-md-4">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Page slug, e.g. my-registrations"
                        value={cfg.member_link?.page || ''}
                        onChange={e => update('member_link', {
                          ...(cfg.member_link || {}), page: e.target.value,
                        })}
                      />
                    </div>
                    <div className="col-md-3">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Icon (bi-person-check)"
                        value={cfg.member_link?.icon || ''}
                        onChange={e => update('member_link', {
                          ...(cfg.member_link || {}), icon: e.target.value,
                        })}
                      />
                    </div>
                  </div>
                </div>

                {msg && (
                  <div className={`alert alert-${msg.type === 'success' ? 'success' : 'danger'} py-2 small mb-0`}>
                    {msg.text}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="modal-footer border-top">
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-sm"
              style={{ background: theme.primary, color: '#fff', border: 'none' }}
              onClick={handleSave}
              disabled={saving || isLoading}
            >
              {saving
                ? <><span className="spinner-border spinner-border-sm me-1" /> Saving…</>
                : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
